// qs-LayersPanel.vue
<template>
  <div class="layers-panel">
    <LayerSection
      @layer-selected="handleLayerSelected"
      @layer-added="handleLayerAdded"
      @layer-duplicated="handleLayerDuplicated"
      @layer-deleted="handleLayerDeleted"
      @layer-move="handleLayerMove"
      @layer-visibility-changed="handleLayerVisibilityChanged"
      @layer-lock-changed="handleLayerLockChanged"
      @layer-renamed="handleLayerRenamed"
      @layer-reordered="handleLayerReordered"
    />
    <SilhouetteSection
      @silhouette-changed="handleSilhouetteChanged"
      @silhouette-visibility-changed="handleSilhouetteVisibilityChanged"
      @silhouette-color-changed="handleSilhouetteColorChanged"
    />
    <BackgroundColorControl
      @background-color-changed="handleBackgroundColorChanged"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch } from 'vue'
import BackgroundColorControl from '@/components/panels/qs-BackgroundColorControl.vue'
import LayerSection from '@/components/panels/qs-LayerSection.vue'
import SilhouetteSection from '@/components/panels/qs-SilhouetteSection.vue'
import { emitPanelContentChanged } from '@/composables/qs-useEventBus'
import { useLayerStore } from '@/stores/qs-store-layers'
import { usePanelStore } from '@/stores/qs-store-panels'
import { useSilhouetteStore } from '@/stores/qs-store-silhouette'
import type { Layer } from '@/types/qs-types-index'
import type { Silhouette } from '@/types/qs-type-silhouette'

interface Props {
  showBackgroundLayer?: boolean
}

withDefaults(defineProps<Props>(), {
  showBackgroundLayer: true
})

const emit = defineEmits<{
  'layer-selected': [layerId: string]
  'layer-added': [layer: Layer]
  'layer-duplicated': [layer: Layer]
  'layer-deleted': [layerId: string]
  'layer-move': [layerId: string]
  'layer-visibility-changed': [layerId: string, visible: boolean]
  'layer-lock-changed': [layerId: string, locked: boolean]
  'layer-renamed': [layerId: string, newName: string]
  'layer-reordered': [fromIndex: number, toIndex: number]
  'background-color-changed': [color: string]
  'silhouette-changed': [silhouette: Silhouette]
  'silhouette-visibility-changed': [visible: boolean]
  'silhouette-color-changed': [color: string]
}>()

const panelStore = usePanelStore()
const layerStore = useLayerStore()
const silhouetteStore = useSilhouetteStore()

const handleLayerSelected = (layerId: string) => {
  emit('layer-selected', layerId)
}

const handleLayerAdded = (layer: Layer) => {
  emit('layer-added', layer)
}

const handleLayerDuplicated = (layer: Layer) => {
  emit('layer-duplicated', layer)
}

const handleLayerDeleted = (layerId: string) => {
  emit('layer-deleted', layerId)
}

const handleLayerMove = (layerId: string) => {
  emit('layer-move', layerId)
}

const handleLayerVisibilityChanged = (layerId: string, visible: boolean) => {
  emit('layer-visibility-changed', layerId, visible)
}

const handleLayerLockChanged = (layerId: string, locked: boolean) => {
  emit('layer-lock-changed', layerId, locked)
}

const handleLayerRenamed = (layerId: string, newName: string) => {
  emit('layer-renamed', layerId, newName)
}

const handleLayerReordered = (fromIndex: number, toIndex: number) => {
  emit('layer-reordered', fromIndex, toIndex)
}

const handleSilhouetteChanged = (silhouette: Silhouette) => {
  emit('silhouette-changed', silhouette)
}

const handleSilhouetteVisibilityChanged = (visible: boolean) => {
  emit('silhouette-visibility-changed', visible)
}

const handleSilhouetteColorChanged = (color: string) => {
  emit('silhouette-color-changed', color)
}

const handleBackgroundColorChanged = (color: string) => {
  emit('background-color-changed', color)
}

watch(
  () => layerStore.layers.length,
  () => {
    panelStore.updateLayerCount()
    panelStore.invalidatePanelDimensions('layers')
    emitPanelContentChanged({ panelId: 'layers' })
  }
)

onMounted(async () => {
  try {
    panelStore.invalidatePanelDimensions('layers')
    await silhouetteStore.loadSilhouettes()
  } catch (error) {
    console.error('Failed to initialize layers panel:', error)
  }
})
</script>

<style scoped>
.layers-panel {
  display: flex;
  flex-direction: column;
  width: auto;
  min-height: theme('spacing.24');
  background: transparent;
  gap: 0;
  overflow: visible;
}

@media (max-width: 768px) {
  .layers-panel {
    min-width: theme('spacing.48');
  }
}

.layers-panel > * {
  border: none;
  border-radius: theme('borderRadius.DEFAULT');
  background: theme('colors.creme.lighter');
  margin-bottom: theme('spacing.px');
  overflow: hidden;
}

.layers-panel > :last-child {
  margin-bottom: 0;
}
</style>
